import { Head, useForm } from "@inertiajs/react";
import { AlertCircle, Brain, Database, FileText, Lock, PackageCheck, ScanText, ShieldCheck, Sparkles, Upload } from "lucide-react";
import type { FormEventHandler } from "react";
import Dropzone from "shadcn-dropzone";

import InputError from "@/components/input-error";
import QuickProcessingOverlay from "@/components/quick-request/quick-processing-overlay";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FilePreview } from "@/components/ui/file-preview";
import { Label } from "@/components/ui/label";
import type { LoadingState } from "@/components/ui/multi-step-loader";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Solicitudes Compras",
    href: "/purchase_requests",
  },
  {
    title: "Crear desde documento",
    href: "/purchase_requests/upload_new",
  },
];

export default function UploadNew() {
  const { data, setData, post, processing, errors, reset } = useForm({
    pdf: null as File | null,
  });

  const loadingStates: LoadingState[] = [
    { text: "Leyendo documento", icon: FileText },
    { text: "Extrayendo campos", icon: ScanText },
    { text: "Validando datos", icon: ShieldCheck },
    { text: "Creando Solicitud de compra", icon: PackageCheck },
  ];

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    if (!data.pdf) {
      return;
    }

    post("/purchase_requests/create_from_pdf", {
      forceFormData: true,
      onFinish: () => reset("pdf"),
    });
  };

  const handleFileChange = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0] || null;
    setData("pdf", file);
  };

  const handleFileRemove = () => {
    setData("pdf", null);
  };

  // Nota: eliminamos el auto-submit para evitar bucles de navegación y dar control explícito al usuario.



  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Crear desde documento" />
      <div className="flex h-full flex-1 flex-col p-6">
        <div className="w-full max-w-2xl mx-auto">
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>¿Qué va a pasar?</AlertTitle>
            <AlertDescription>
              Sube el documento (PDF o imagen) de requerimientos. La IA lo analiza, extrae campos clave y los guarda como una Solicitud de compra. Luego podrás revisar, editar y enviar solicitudes de cotización a tus proveedores.
            </AlertDescription>
          </Alert>
          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles className="h-5 w-5 text-indigo-500" />
                  <CardTitle className="leading-tight">Extrae tu Solicitud de compra con IA</CardTitle>
                </div>
                <div className="hidden md:flex items-center gap-2 text-muted-foreground">
                  <ScanText className="h-4 w-4" />
                  <Brain className="h-4 w-4" />
                  <Database className="h-4 w-4" />
                </div>
              </div>
              <CardDescription>
                La IA leerá tu documento y convertirá los datos en una Solicitud de compra lista para revisar. <strong className="text-black underline">Si tienes algun error en el procesamiento, trata de subir el documento nuevamente.</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <form className="flex flex-col gap-4" onSubmit={submit}>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label className="sr-only">Documento de Solicitud de compra</Label>
                    <div className="w-full">
                      <Dropzone
                        onDrop={handleFileChange}
                        accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                        maxFiles={1}
                        disabled={processing}
                        containerClassName="w-full"
                        dropZoneClassName="group relative w-full h-48 rounded-2xl border border-solid border-border outline-none bg-gradient-to-br from-muted/40 to-muted/10 hover:from-accent/50 hover:to-accent/20 transition-shadow shadow-sm hover:shadow-md overflow-hidden"
                      >
                        {(dz) => (
                          <>
                            {/* Decorative grid + glow overlays */}
                            <div className="pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(600px_circle_at_center,black,transparent)] bg-[linear-gradient(to_right,rgba(120,119,198,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(120,119,198,0.08)_1px,transparent_1px)] bg-[size:14px_14px]" />
                            <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(400px_circle_at_center,rgba(99,102,241,0.14),transparent_60%)]" />

                            <div className="relative flex h-full w-full items-center justify-center">
                              <div className="flex flex-col items-center text-center gap-2 px-6">
                                <div
                                  className={`h-12 w-12 rounded-full ring-1 flex items-center justify-center shadow-sm transition-all ${
                                    dz.isDragAccept
                                      ? "bg-indigo-500/10 ring-indigo-400/50"
                                      : dz.isDragReject
                                      ? "bg-destructive/10 ring-destructive/40"
                                      : "bg-background/60 ring-border"
                                  }`}
                                >
                                  <Upload
                                    className={`h-5 w-5 transition-colors ${
                                      dz.isDragAccept
                                        ? "text-indigo-500"
                                        : dz.isDragReject
                                        ? "text-destructive"
                                        : "text-muted-foreground"
                                    }`}
                                  />
                                </div>

                                <div className="text-sm font-medium">
                                  {dz.isDragActive ? "Suelta para procesar" : "Suelta tu documento"}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {dz.isDragActive ? "Procesaremos automáticamente" : "o haz clic para seleccionarlo"}
                                </p>
                                <p className="text-[11px] text-muted-foreground/80">
                                  Formatos: .pdf, .jpg, .jpeg, .png · 1 archivo
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </Dropzone>

                      {data.pdf && (
                        <div className="flex flex-col gap-2 w-full h-fit mt-2 pb-2">
                          <div className="w-full">
                            <FilePreview
                              file={data.pdf}
                              onRemove={handleFileRemove}
                              disabled={processing}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5" /> Procesamos tu archivo de forma segura.
                    </p>
                    <InputError message={errors.pdf} />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="submit"
                    disabled={!data.pdf || processing}
                    tabIndex={2}
                  >
                    {processing ? "Procesando…" : "Procesar"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.history.back()}
                    disabled={processing}
                    tabIndex={3}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* AI Multi-step overlay (unificado) */}
      <QuickProcessingOverlay
        visible={processing}
        states={loadingStates}
        duration={1400}
        title="Analizando documento con IA…"
        blur="md"
      />
    </AppLayout>
  );
}
